import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckSquare, Square, Camera, ArrowLeft, Save, X, AlertTriangle, Clock, CheckCircle, SkipForward, Minus, XCircle, Wind } from 'lucide-react';
import { openNativeCamera, fileToDataUrl } from '../../utils/camera.utils';
import { Camera as CameraComponent } from '../Camera';
import { isIOS } from '../../utils/platform.utils';
import { CustomSelect } from '../common/CustomSelect';

// ============================================================
// INTERFACES
// ============================================================

interface TemplateItem {
  item_id: number;
  item_name: string;
  item_category: string;
  requires_photo: boolean;
  requires_notes: boolean;
  service_interval_hours: number | null;
  item_type: 'check' | 'service';
  allow_clean?: boolean; // air filters: offer a "Cleaned" option
  display_order: number;
  last_service_hours?: number | null;
}

interface InspectionTemplate {
  template_id: number;
  template_name: string;
  description: string;
  items: TemplateItem[];
}

type ItemStatus = 'pending' | 'completed' | 'cleaned' | 'not_needed' | 'issue_found'; // 'completed'='Changed/Done'; 'cleaned'=air filter blown out/washed

interface InspectionItemData {
  inspection_item_id?: number; // For existing items when resuming
  template_item_id: number;
  item_status: ItemStatus;
  item_notes: string;
  photos: File[]; // PENDING photos only — not yet uploaded to the server
  uploaded_count: number; // photos already attached server-side (item_photos)
}

interface Equipment {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  equipment_type: string;
  hours_last_serviced?: number;
  latest_telematics?: {
    hours: number;
  };
}

interface EquipmentIssue {
  issue_id: number;
  issue_description: string;
  severity: string;
  priority: string;
  parts_needed: string | null;
  parts_status: string;
  reported_date: string;
  reported_by: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// fetch that cannot hang forever. The 7/29 lost-draft incident was a photo
// POST that stalled mid-upload with no timeout — the UI spun until the user
// gave up and nothing was saved. Every write in this page goes through this.
const fetchWithTimeout = (input: RequestInfo, init: RequestInit, timeoutMs: number): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(input, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
};

// Downscale a camera photo before upload (max 1600px, JPEG q0.8). Full-res
// phone photos are 5-10 MB each; over field cell service that is exactly the
// payload that stalls. Falls back to the original file if anything fails.
const compressImage = (file: File): Promise<File> => new Promise((resolve) => {
  const objectUrl = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(objectUrl);
    const MAX_DIM = 1600;
    const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) { resolve(file); return; }
    ctx.drawImage(img, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (blob && blob.size > 1000 && blob.size < file.size) {
        resolve(new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }));
      } else {
        resolve(file);
      }
    }, 'image/jpeg', 0.8);
  };
  img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
  img.src = objectUrl;
});

// ============================================================
// TRANSLATIONS
// ============================================================

const translations = {
  en: {
    title: 'Equipment Inspection',
    back: 'Back to Machine Hub',
    currentHours: 'Current Engine Hours',
    overallNotes: 'Overall Notes',
    openIssues: 'Open Issues for This Equipment',
    completedIssues: 'Completed Issues from This Inspection',
    noOpenIssues: 'No open issues',
    itemStatus: 'Item Status',
    completed: 'Changed/Done',
    notNeeded: 'Not Needed',
    issueFound: 'Issue Found',
    pending: 'Pending',
    // Check item labels (Pass/Fail)
    checkItemPass: 'Pass',
    checkItemFail: 'Fail',
    // Service item labels (Changed/Not Changed)
    serviceItemChanged: 'Changed',
    serviceItemCleaned: 'Cleaned',
    serviceItemNotChanged: 'Not Changed',
    // Service due indicators
    serviceDueNow: 'DUE NOW',
    serviceDueSoon: 'Due Soon',
    serviceOk: 'OK',
    neverServiced: 'never serviced',
    hrsOverdue: 'hrs overdue',
    hrs: 'hrs',
    dueIn: 'due in',
    photoRequired: 'Photo Required',
    notesRequired: 'Notes Required',
    takePhoto: 'Take Photo',
    removePhoto: 'Remove Photo',
    photoTaken: 'Photo Taken',
    notesPlaceholder: 'Enter notes...',
    saveAsDraft: 'Save as Draft',
    draftSavedOk: 'Draft saved — everything is on the server.',
    draftSavedPhotosPending: 'Draft saved, but some photos did not upload — they will retry on the next save.',
    draftCreateFailed: 'Could not start the draft — check your connection and try again.',
    photoUploadFailed: 'Photo upload failed — it will retry when you save.',
    photosPendingBlock: 'Some photos have not uploaded yet — check your connection and tap Submit again. Nothing was lost.',
    uploadedBadge: 'uploaded',
    completeInspection: 'Submit Inspection',
    saving: 'Submitting...',
    autoSaving: 'Auto-saving...',
    lastSaved: 'Last saved',
    cancel: 'Cancel',
    success: 'Inspection Completed!',
    successMessage: 'Equipment inspection has been saved successfully.',
    backToHub: 'Back to Machine Hub',
    loading: 'Loading inspection template...',
    noTemplate: 'No inspection template found for this equipment type',
    resumeInspection: 'Resume Inspection?',
    resumeMessage: 'An inspection is already in progress for this equipment. Would you like to resume it?',
    resume: 'Resume',
    startNew: 'Start New',
    markFixed: 'Mark as Fixed',
    fixDescription: 'Fix description',
    priority: 'Priority',
    partsStatus: 'Parts Status',
    partsNeeded: 'Parts Needed',
    createIssue: 'Create Issue',
    issueDescription: 'Issue Description',
    severity: 'Severity',
    low: 'Low',
    medium: 'Medium',
    high: 'High'
  },
  es: {
    title: 'Inspección de Equipo',
    back: 'Volver al Centro de Máquinas',
    currentHours: 'Horas Actuales del Motor',
    overallNotes: 'Notas Generales',
    openIssues: 'Problemas Abiertos para Este Equipo',
    completedIssues: 'Problemas Completados de Esta Inspección',
    noOpenIssues: 'No hay problemas abiertos',
    itemStatus: 'Estado del Artículo',
    completed: 'Cambiado/Hecho',
    notNeeded: 'No Necesario',
    issueFound: 'Problema Encontrado',
    pending: 'Pendiente',
    // Check item labels (Pass/Fail)
    checkItemPass: 'Pasa',
    checkItemFail: 'Falla',
    // Service item labels (Changed/Not Changed)
    serviceItemChanged: 'Cambiado',
    serviceItemCleaned: 'Limpiado',
    serviceItemNotChanged: 'No Cambiado',
    // Service due indicators
    serviceDueNow: 'VENCE AHORA',
    serviceDueSoon: 'Vence Pronto',
    serviceOk: 'OK',
    neverServiced: 'nunca serviciado',
    hrsOverdue: 'hrs atrasado',
    hrs: 'hrs',
    dueIn: 'vence en',
    photoRequired: 'Foto Requerida',
    notesRequired: 'Notas Requeridas',
    takePhoto: 'Tomar Foto',
    removePhoto: 'Quitar Foto',
    photoTaken: 'Foto Tomada',
    notesPlaceholder: 'Ingresar notas...',
    saveAsDraft: 'Guardar como Borrador',
    draftSavedOk: 'Borrador guardado — todo está en el servidor.',
    draftSavedPhotosPending: 'Borrador guardado, pero algunas fotos no se subieron — se reintentarán al guardar de nuevo.',
    draftCreateFailed: 'No se pudo iniciar el borrador — revise su conexión e intente de nuevo.',
    photoUploadFailed: 'La foto no se subió — se reintentará al guardar.',
    photosPendingBlock: 'Algunas fotos aún no se han subido — revise su conexión y toque Enviar de nuevo. No se perdió nada.',
    uploadedBadge: 'subidas',
    completeInspection: 'Enviar Inspección',
    saving: 'Enviando...',
    autoSaving: 'Guardando automáticamente...',
    lastSaved: 'Última guardado',
    cancel: 'Cancelar',
    success: '¡Inspección Completada!',
    successMessage: 'La inspección del equipo se ha guardado exitosamente.',
    backToHub: 'Volver al Centro',
    loading: 'Cargando plantilla de inspección...',
    noTemplate: 'No se encontró plantilla de inspección para este tipo de equipo',
    resumeInspection: '¿Reanudar Inspección?',
    resumeMessage: 'Ya hay una inspección en progreso para este equipo. ¿Desea reanudarla?',
    resume: 'Reanudar',
    startNew: 'Comenzar Nueva',
    markFixed: 'Marcar como Arreglado',
    fixDescription: 'Descripción de la reparación',
    priority: 'Prioridad',
    partsStatus: 'Estado de Piezas',
    partsNeeded: 'Piezas Necesarias',
    createIssue: 'Crear Problema',
    issueDescription: 'Descripción del Problema',
    severity: 'Severidad',
    low: 'Bajo',
    medium: 'Medio',
    high: 'Alto'
  }
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export const EquipmentInspectionEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const { equipmentId } = useParams<{ equipmentId: string }>();

  // Language
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  // Core state
  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const [template, setTemplate] = useState<InspectionTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Inspection state
  const [inspectionId, setInspectionId] = useState<number | null>(null);
  const [currentHours, setCurrentHours] = useState('');
  // Service date — defaults to today, can be back-dated for late entries
  const [serviceDate, setServiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [overallNotes, setOverallNotes] = useState('');
  const [inspectionItems, setInspectionItems] = useState<Map<number, InspectionItemData>>(new Map());
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Issues state
  const [openIssues, setOpenIssues] = useState<EquipmentIssue[]>([]);
  const [completedIssues, setCompletedIssues] = useState<EquipmentIssue[]>([]);

  // Modals
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [currentIssueItem, setCurrentIssueItem] = useState<{ item_id: number; item_name: string } | null>(null);
  const [showFixIssueModal, setShowFixIssueModal] = useState(false);
  const [currentFixingIssue, setCurrentFixingIssue] = useState<EquipmentIssue | null>(null);

  // Camera state
  const [currentPhotoItemId, setCurrentPhotoItemId] = useState<number | null>(null);
  const [cameraCooldown, setCameraCooldown] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const t = translations[language];

  // Live mirror of the form state, so async work (draft creation, photo
  // uploads, flushes) always reads current values instead of stale closures.
  const stateRef = useRef({ inspectionId, template, currentHours, overallNotes, inspectionItems, serviceDate });
  stateRef.current = { inspectionId, template, currentHours, overallNotes, inspectionItems, serviceDate };
  // Single-flight guard: only ever one draft-create in flight.
  const draftPromiseRef = useRef<Promise<number> | null>(null);
  // Photos currently mid-upload (so a manual flush can't double-send them).
  const uploadingRef = useRef<Set<File>>(new Set());
  // Skip the lazy draft-create until the initial load has settled.
  const initialLoadDone = useRef(false);

  // ============================================================
  // EFFECTS
  // ============================================================

  // Language change listener
  useEffect(() => {
    const langData = localStorage.getItem('language');
    if (langData === 'en' || langData === 'es') {
      setLanguage(langData);
    }

    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  // Initial load
  useEffect(() => {
    if (equipmentId) {
      loadEquipmentAndCheckOpenInspection();
    }
  }, [equipmentId]);

  // Auto-save: debounced 3s after any change (kiosk-style, replaces the old
  // 30s interval — a mechanic shouldn't be 30 seconds of work away from safe).
  useEffect(() => {
    if (!inspectionId || !hasChanges) return;

    const timer = setTimeout(() => {
      autoSaveInspection();
    }, 3000);

    return () => clearTimeout(timer);
  }, [inspectionId, hasChanges, currentHours, overallNotes, inspectionItems, serviceDate]);

  // Mark as having changes when form updates
  useEffect(() => {
    if (inspectionId) {
      setHasChanges(true);
    }
  }, [currentHours, overallNotes, inspectionItems, serviceDate]);

  // Lazy draft-first: the first real edit creates the server draft, so an
  // accidental page open leaves no litter but nothing typed is ever only
  // local for more than a moment. Resumed inspections already have an id.
  useEffect(() => {
    if (loading) return;
    if (!initialLoadDone.current) { initialLoadDone.current = true; return; }
    if (!inspectionId && template) {
      ensureDraft().catch((err) => {
        console.error('Draft create failed:', err);
        setError(t.draftCreateFailed);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHours, overallNotes, inspectionItems, serviceDate, loading]);

  // ============================================================
  // LOAD DATA
  // ============================================================

  const loadEquipmentAndCheckOpenInspection = async () => {
    setLoading(true);
    try {
      // Load equipment details
      const equipmentResponse = await fetch(`${API_BASE_URL}/api/equipment/${equipmentId}/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (equipmentResponse.ok) {
        const equipmentData = await equipmentResponse.json();
        setEquipment(equipmentData.data);
        // Auto-populate current hours from Traccar telematics if available and field is still blank
        const telematicsHours = equipmentData.data?.latest_telematics?.hours;
        if (telematicsHours != null && !currentHours) {
          setCurrentHours(String(Math.round(telematicsHours)));
        }
      }

      // Check for open inspection
      const openInspectionResponse = await fetch(`${API_BASE_URL}/api/equipment/${equipmentId}/open-inspection`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (openInspectionResponse.ok) {
        const openInspectionData = await openInspectionResponse.json();

        if (openInspectionData.data) {
          // An open draft always resumes — there is deliberately no "start
          // new" escape hatch, so one machine can never grow two drafts.
          await hydrateInspection(openInspectionData.data.inspection_id);
        } else {
          // No open inspection, load template for new one (the draft row is
          // created lazily on the first edit — see ensureDraft).
          await loadTemplate();
        }
      }
    } catch (error) {
      console.error('Failed to load equipment:', error);
      setError('Failed to load equipment information');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async () => {
    try {
      const templateResponse = await fetch(`${API_BASE_URL}/api/equipment/${equipmentId}/inspection-template`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (templateResponse.ok) {
        const templateData = await templateResponse.json();
        setTemplate(templateData.data);

        // Initialize inspection items
        const itemsMap = new Map<number, InspectionItemData>();
        templateData.data.items.forEach((item: TemplateItem) => {
          itemsMap.set(item.item_id, {
            template_item_id: item.item_id,
            item_status: 'pending',
            item_notes: '',
            photos: [],
            uploaded_count: 0
          });
        });
        setInspectionItems(itemsMap);
      }

      // Load open issues for this equipment
      const issuesResponse = await fetch(`${API_BASE_URL}/api/equipment/${equipmentId}/issues?status=open`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (issuesResponse.ok) {
        const issuesData = await issuesResponse.json();
        setOpenIssues(issuesData.data || []);
      }
    } catch (error) {
      console.error('Failed to load template:', error);
      setError('Failed to load inspection template');
    }
  };

  // Load an existing open draft into the form (auto-resume).
  const hydrateInspection = async (inspectionIdToLoad: number) => {
    // First load the template
    const templateResponse = await fetch(`${API_BASE_URL}/api/equipment/${equipmentId}/inspection-template`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
      }
    });

    if (!templateResponse.ok) {
      const errorData = await templateResponse.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to load inspection template');
    }

    const templateData = await templateResponse.json();

    if (!templateData.success || !templateData.data) {
      throw new Error('No inspection template found for this equipment type');
    }
    setTemplate(templateData.data);

    // Then load the inspection data
    const response = await fetch(`${API_BASE_URL}/api/equipment/inspection/${inspectionIdToLoad}/with-issues`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to resume inspection');
    }

    const data = await response.json();

    // Set inspection data
    setInspectionId(inspectionIdToLoad);
    setCurrentHours(data.data.inspection.current_hours?.toString() || '');
    setOverallNotes(data.data.inspection.overall_notes || '');
    if (data.data.inspection.inspection_date) {
      setServiceDate(String(data.data.inspection.inspection_date).slice(0, 10));
    }
    setLastSaved(new Date(data.data.inspection.last_updated));

    // Set inspection items from saved data - merge with template items
    const itemsMap = new Map<number, InspectionItemData>();

    // First, initialize all template items
    templateData.data.items.forEach((templateItem: TemplateItem) => {
      itemsMap.set(templateItem.item_id, {
        template_item_id: templateItem.item_id,
        item_status: 'pending',
        item_notes: '',
        photos: [],
        uploaded_count: 0
      });
    });

    // Then, update with saved data
    data.data.items.forEach((item: any) => {
      let uploaded = 0;
      try { uploaded = JSON.parse(item.item_photos || '[]').length; } catch { uploaded = 0; }
      itemsMap.set(item.template_item_id, {
        inspection_item_id: item.inspection_item_id,
        template_item_id: item.template_item_id,
        item_status: item.item_status as ItemStatus,
        item_notes: item.item_notes || '',
        photos: [],
        uploaded_count: uploaded
      });
    });
    setInspectionItems(itemsMap);

    // Set issues
    setOpenIssues(data.data.open_issues || []);
    setCompletedIssues(data.data.completed_issues || []);

    setHasChanges(false);
  };

  // ============================================================
  // DRAFT-FIRST SAVE CORE
  // ============================================================

  // PUT the current form state onto an existing draft. Reads from stateRef so
  // it is safe to call right after ensureDraft (before React re-renders).
  const saveInspectionCore = useCallback(async (id: number): Promise<boolean> => {
    const st = stateRef.current;
    try {
      const itemsToUpdate = Array.from(st.inspectionItems.values())
        .filter((item) => item.inspection_item_id) // Only update existing items
        .map((item) => ({
          inspection_item_id: item.inspection_item_id,
          item_status: item.item_status,
          item_notes: item.item_notes
        }));

      const response = await fetchWithTimeout(`${API_BASE_URL}/api/equipment/inspection/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_hours: st.currentHours ? parseInt(st.currentHours) : null,
          overall_notes: st.overallNotes,
          items: itemsToUpdate,
          inspection_date: st.serviceDate || undefined
        })
      }, 30000);

      if (response.ok) {
        setLastSaved(new Date());
        setHasChanges(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Save failed:', error);
      return false;
    }
  }, []);

  const autoSaveInspection = useCallback(async () => {
    const id = stateRef.current.inspectionId;
    if (!id) return;
    setAutoSaving(true);
    try {
      await saveInspectionCore(id);
    } finally {
      setAutoSaving(false);
    }
  }, [saveInspectionCore]);

  // Create the server draft if one doesn't exist yet (single-flight). The
  // create carries the current form state but NO photos — those go through
  // POST /inspection/:id/photos one at a time. The backend is idempotent:
  // if an open draft already exists for this machine it returns that id, so
  // this can never fork a duplicate.
  const ensureDraft = useCallback(async (): Promise<number | null> => {
    const st = stateRef.current;
    if (st.inspectionId) return st.inspectionId;
    if (!st.template) return null;

    if (!draftPromiseRef.current) {
      draftPromiseRef.current = (async () => {
        const cur = stateRef.current;
        const formData = new FormData();
        formData.append('equipment_id', equipmentId || '');
        formData.append('template_id', cur.template!.template_id.toString());
        formData.append('current_hours', cur.currentHours);
        formData.append('overall_notes', cur.overallNotes);
        formData.append('is_draft', 'true');
        if (cur.serviceDate) formData.append('inspection_date', cur.serviceDate);
        const itemsArray = Array.from(cur.inspectionItems.values()).map(item => ({
          template_item_id: item.template_item_id,
          item_status: item.item_status,
          item_notes: item.item_notes,
          is_completed: item.item_status === 'completed' // Backward compatibility
        }));
        formData.append('items', JSON.stringify(itemsArray));

        const response = await fetchWithTimeout(`${API_BASE_URL}/api/equipment/inspection`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
          },
          body: formData
        }, 60000);
        if (!response.ok) throw new Error('Failed to create draft');
        const data = await response.json();
        const id: number = data.data.inspection_id;
        const resumedExisting: boolean = !!data.data.resumed_existing;

        // Pull the server-side item ids (and, if we landed on a pre-existing
        // draft, its saved content) into the local map. Local edits win.
        try {
          const withIssuesResp = await fetch(`${API_BASE_URL}/api/equipment/inspection/${id}/with-issues`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('bfa_token')}` }
          });
          if (withIssuesResp.ok) {
            const d = await withIssuesResp.json();
            setInspectionItems(prev => {
              const updated = new Map(prev);
              (d.data.items || []).forEach((srv: any) => {
                const local = updated.get(srv.template_item_id);
                if (!local) return;
                local.inspection_item_id = srv.inspection_item_id;
                try { local.uploaded_count = JSON.parse(srv.item_photos || '[]').length; } catch { /* keep */ }
                if (local.item_status === 'pending' && srv.item_status && srv.item_status !== 'pending') {
                  local.item_status = srv.item_status as ItemStatus;
                }
                if (!local.item_notes && srv.item_notes) local.item_notes = srv.item_notes;
                updated.set(srv.template_item_id, local);
              });
              return updated;
            });
            setOpenIssues(d.data.open_issues || []);
            setCompletedIssues(d.data.completed_issues || []);
          }
        } catch { /* ids arrive on next hydrate; autosave just skips items without ids */ }

        setInspectionId(id);
        setLastSaved(new Date());
        // If we adopted an existing draft, push our local state onto it now.
        if (resumedExisting) await saveInspectionCore(id);
        return id;
      })().catch((e) => {
        draftPromiseRef.current = null; // allow retry
        throw e;
      });
    }
    return draftPromiseRef.current;
  }, [equipmentId, saveInspectionCore]);

  // Upload one photo to the draft, compressed, with a hard timeout. On
  // failure the file stays in the item's pending list and is retried by the
  // next Save-as-Draft / Submit.
  const uploadItemPhoto = useCallback(async (itemId: number, file: File): Promise<boolean> => {
    if (uploadingRef.current.has(file)) return true; // already in flight
    uploadingRef.current.add(file);
    try {
      const id = await ensureDraft();
      if (!id) throw new Error('No draft to attach the photo to');
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append(`photo_${itemId}_0`, compressed, compressed.name);
      const response = await fetchWithTimeout(`${API_BASE_URL}/api/equipment/inspection/${id}/photos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('bfa_token')}` },
        body: formData
      }, 90000);
      if (!response.ok) throw new Error(`Photo upload failed (${response.status})`);
      setInspectionItems(prev => {
        const updated = new Map(prev);
        const item = updated.get(itemId);
        if (item) {
          const idx = item.photos.indexOf(file);
          if (idx >= 0) item.photos.splice(idx, 1);
          item.uploaded_count += 1;
          updated.set(itemId, item);
        }
        return updated;
      });
      return true;
    } catch (err) {
      console.error('Photo upload failed (kept pending):', err);
      setError(t.photoUploadFailed);
      return false;
    } finally {
      uploadingRef.current.delete(file);
    }
  }, [ensureDraft, t.photoUploadFailed]);

  // Retry every pending photo. Returns true only if nothing is left pending.
  const flushPendingPhotos = useCallback(async (): Promise<boolean> => {
    let allOk = true;
    const entries = Array.from(stateRef.current.inspectionItems.entries());
    for (const [itemId, item] of entries) {
      for (const file of [...item.photos]) {
        if (uploadingRef.current.has(file)) continue; // in flight — counts as handled
        const ok = await uploadItemPhoto(itemId, file);
        if (!ok) allOk = false;
      }
    }
    return allOk;
  }, [uploadItemPhoto]);

  // ============================================================
  // ITEM STATUS CHANGES
  // ============================================================

  const handleItemStatusChange = (itemId: number, status: ItemStatus) => {
    setInspectionItems(prev => {
      const updated = new Map(prev);
      const item = updated.get(itemId);
      if (item) {
        item.item_status = status;

        // If marking as issue_found, show issue creation modal
        if (status === 'issue_found' && template) {
          const templateItem = template.items.find(ti => ti.item_id === itemId);
          if (templateItem) {
            setCurrentIssueItem({ item_id: itemId, item_name: templateItem.item_name });
            setShowIssueModal(true);
          }
        }

        updated.set(itemId, item);
      }
      return updated;
    });
  };

  const handleItemNotesChange = (itemId: number, notes: string) => {
    setInspectionItems(prev => {
      const updated = new Map(prev);
      const item = updated.get(itemId);
      if (item) {
        item.item_notes = notes;
        updated.set(itemId, item);
      }
      return updated;
    });
  };

  // ============================================================
  // CAMERA
  // ============================================================

  const handleTakePhoto = async (itemId: number) => {
    if (cameraCooldown) return;

    setCurrentPhotoItemId(itemId);

    if (isIOS()) {
      // iOS: Use native camera
      try {
        setCameraCooldown(true);
        await openNativeCamera(async (file) => {
          if (file && file instanceof File) {
            setInspectionItems(prev => {
              const updated = new Map(prev);
              const item = updated.get(itemId);
              if (item) {
                item.photos.push(file);
                updated.set(itemId, item);
              }
              return updated;
            });
            // Upload right away — a photo should never wait for the final save.
            void uploadItemPhoto(itemId, file);
          }
        });
        setTimeout(() => setCameraCooldown(false), 1000);
      } catch (err) {
        console.error('Error capturing photo:', err);
        setCameraCooldown(false);
      } finally {
        setCurrentPhotoItemId(null);
      }
    } else {
      // Android: Use custom camera component
      setShowCamera(true);
    }
  };

  const handleCameraCapture = async (imageDataUrl: string) => {
    setShowCamera(false);

    try {
      // Convert base64 to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });

      const itemId = currentPhotoItemId;
      if (itemId !== null) {
        setInspectionItems(prev => {
          const updated = new Map(prev);
          const item = updated.get(itemId);
          if (item) {
            item.photos.push(file);
            updated.set(itemId, item);
          }
          return updated;
        });
        // Upload right away — a photo should never wait for the final save.
        void uploadItemPhoto(itemId, file);
      }

      setCameraCooldown(true);
      setTimeout(() => setCameraCooldown(false), 1000);
    } catch (err) {
      console.error('Error processing camera capture:', err);
      setCameraCooldown(false);
    } finally {
      setCurrentPhotoItemId(null);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    setCurrentPhotoItemId(null);
    setCameraCooldown(true);
    setTimeout(() => setCameraCooldown(false), 500);
  };

  const handleRemovePhoto = (itemId: number, photoIndex: number) => {
    setInspectionItems(prev => {
      const updated = new Map(prev);
      const item = updated.get(itemId);
      if (item) {
        item.photos.splice(photoIndex, 1);
        updated.set(itemId, item);
      }
      return updated;
    });
  };

  // ============================================================
  // SAVE INSPECTION
  // ============================================================

  // Save as Draft = make sure the draft exists, flush the form state, retry
  // any pending photos. Never creates a second inspection.
  const handleSaveAsDraft = async () => {
    if (!template || !equipment) return;

    setSaving(true);
    setError(null);
    try {
      const id = await ensureDraft();
      if (!id) throw new Error('Failed to save inspection as draft');

      const savedOk = await saveInspectionCore(id);
      if (!savedOk) throw new Error('Failed to save inspection as draft');

      const photosOk = await flushPendingPhotos();
      alert(photosOk ? t.draftSavedOk : t.draftSavedPhotosPending);
    } catch (error) {
      console.error('Error saving draft:', error);
      setError('Failed to save inspection as draft');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteInspection = async () => {
    setSaving(true);
    setError(null);
    try {
      const id = await ensureDraft();
      if (!id) {
        throw new Error('Failed to create inspection. Please try again.');
      }

      // Flush form state and every pending photo BEFORE closing — a submit
      // must never succeed with photos silently missing.
      const savedOk = await saveInspectionCore(id);
      if (!savedOk) throw new Error('Could not save your changes — check your connection and try again. Nothing was lost.');

      const photosOk = await flushPendingPhotos();
      if (!photosOk) throw new Error(t.photosPendingBlock);

      // Now close/complete the inspection
      const response = await fetchWithTimeout(`${API_BASE_URL}/api/equipment/inspection/${id}/close`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      }, 60000);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({} as any));
        throw new Error(errorData.error || 'Failed to complete inspection');
      }

      setShowSuccess(true);
    } catch (error) {
      console.error('Error completing inspection:', error);
      setError(error instanceof Error ? error.message : 'Failed to complete inspection');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // ISSUE MANAGEMENT
  // ============================================================

  const handleCreateIssue = async (issueData: {
    description: string;
    severity: string;
    priority: string;
    parts_needed: string;
    parts_status: string;
  }) => {
    if (!currentIssueItem) return;

    try {
      // Check if this is an "Other Issue" (item_id = 0) or linked to an inspection item
      const isOtherIssue = currentIssueItem.item_id === 0;

      let response;

      if (isOtherIssue) {
        // Create a general equipment issue not linked to inspection item
        // Use same endpoint as Daily Reports for consistency
        const formData = new FormData();
        formData.append('equipment_id', equipmentId?.toString() || '');
        formData.append('issue_description', issueData.description);
        formData.append('severity', issueData.severity);

        response = await fetch(`${API_BASE_URL}/api/equipment/issue`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
          },
          body: formData
        });
      } else {
        // Create issue linked to specific inspection item. The draft (and its
        // server-side item ids) is created on demand — no manual save needed.
        const draftId = await ensureDraft();
        const item = stateRef.current.inspectionItems.get(currentIssueItem.item_id);
        if (!draftId || !item?.inspection_item_id) {
          alert('Could not link the issue — check your connection and try again');
          return;
        }

        response = await fetch(`${API_BASE_URL}/api/equipment/inspection-item/${item.inspection_item_id}/create-issue`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            issue_description: issueData.description,
            severity: issueData.severity,
            priority: issueData.priority,
            parts_needed: issueData.parts_needed,
            parts_status: issueData.parts_status
          })
        });
      }

      if (response.ok) {
        // Refresh open issues
        await refreshIssues();
        setShowIssueModal(false);
        setCurrentIssueItem(null);
      }
    } catch (error) {
      console.error('Failed to create issue:', error);
      alert('Failed to create issue');
    }
  };

  const handleFixIssue = async (fixDescription: string) => {
    if (!currentFixingIssue) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/issues/${currentFixingIssue.issue_id}/close-from-inspection`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fix_description: fixDescription })
      });

      if (response.ok) {
        await refreshIssues();
        setShowFixIssueModal(false);
        setCurrentFixingIssue(null);
      }
    } catch (error) {
      console.error('Failed to fix issue:', error);
      alert('Failed to mark issue as fixed');
    }
  };

  const refreshIssues = async () => {
    if (!inspectionId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/inspection/${inspectionId}/with-issues`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOpenIssues(data.data.open_issues || []);
        setCompletedIssues(data.data.completed_issues || []);
      }
    } catch (error) {
      console.error('Failed to refresh issues:', error);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  const handleBackToHub = () => {
    navigate('/mechanics/machine-hub');
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        {t.loading}
      </div>
    );
  }

  if (!template) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        {t.noTemplate}
      </div>
    );
  }

  // Group items by category
  const itemsByCategory = template.items.reduce((acc, item) => {
    if (!acc[item.item_category]) {
      acc[item.item_category] = [];
    }
    acc[item.item_category].push(item);
    return acc;
  }, {} as Record<string, TemplateItem[]>);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerTitle}>{t.title}</div>
        </div>
        {equipment && (
          <div style={styles.equipmentInfo}>
            {equipment.equipment_name} - #{equipment.bradanna_equipment_number}
          </div>
        )}
        {lastSaved && (
          <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
            <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
            {t.lastSaved}: {lastSaved.toLocaleTimeString()}
            {autoSaving && ' - ' + t.autoSaving}
          </div>
        )}
      </div>

      <div style={styles.content}>
        {/* Form Section */}
        <div style={styles.formSection}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Service Date</label>
            <input
              type="date"
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              style={styles.input}
            />
            {serviceDate !== new Date().toISOString().slice(0, 10) && (
              <div style={{ marginTop: 6, padding: '6px 10px', background: '#fef3c7', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                ⚠️ Back-dated entry — recording for {new Date(serviceDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            )}
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.currentHours}</label>
            <input
              type="number"
              value={currentHours}
              onChange={(e) => setCurrentHours(e.target.value)}
              style={styles.input}
              placeholder="0"
            />
            {/* Hours since last service calculation */}
            {equipment?.hours_last_serviced && currentHours && parseInt(currentHours) > 0 && (
              <div style={{
                marginTop: '8px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '2px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                  Hours Since Last Service
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: parseInt(currentHours) - equipment.hours_last_serviced >= 100 ? '#ef4444' : '#ea580c'
                }}>
                  {parseInt(currentHours) - equipment.hours_last_serviced} hours
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                  Last serviced at {equipment.hours_last_serviced.toLocaleString()} hours
                </div>
              </div>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>{t.overallNotes}</label>
            <textarea
              value={overallNotes}
              onChange={(e) => setOverallNotes(e.target.value)}
              style={styles.textarea}
              placeholder={t.notesPlaceholder}
            />
          </div>
        </div>

        {/* Open Issues Section */}
        {openIssues.length > 0 && (
          <div style={styles.issuesSection}>
            <h3 style={styles.issuesTitle}>
              <AlertTriangle size={20} color="#f59e0b" />
              {t.openIssues}
            </h3>
            {openIssues.map(issue => (
              <IssueCard
                key={issue.issue_id}
                issue={issue}
                onFix={() => {
                  setCurrentFixingIssue(issue);
                  setShowFixIssueModal(true);
                }}
                language={language}
              />
            ))}
          </div>
        )}

        {/* Inspection Items by Category */}
        {Object.entries(itemsByCategory).map(([category, items]) => (
          <div key={category} style={styles.categorySection}>
            <div style={styles.categoryTitle}>{category}</div>
            {items.map(item => {
              const itemData = inspectionItems.get(item.item_id);
              if (!itemData) return null;

              return (
                <InspectionItem
                  key={item.item_id}
                  item={item}
                  itemData={itemData}
                  currentHours={currentHours ? parseInt(currentHours) : null}
                  onStatusChange={(status) => handleItemStatusChange(item.item_id, status)}
                  onNotesChange={(notes) => handleItemNotesChange(item.item_id, notes)}
                  onTakePhoto={() => handleTakePhoto(item.item_id)}
                  onRemovePhoto={(index) => handleRemovePhoto(item.item_id, index)}
                  language={language}
                />
              );
            })}
          </div>
        ))}

        {/* Add Other Issue Button */}
        <div style={{ marginTop: '24px', marginBottom: '24px' }}>
          <button
            onClick={() => {
              setCurrentIssueItem({ item_id: 0, item_name: 'Other Issue' });
              setShowIssueModal(true);
            }}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <AlertTriangle size={20} />
            Add Other Issue
          </button>
        </div>

        {/* Completed Issues */}
        {completedIssues.length > 0 && (
          <div style={styles.completedIssuesSection}>
            <h3 style={styles.completedIssuesTitle}>
              <CheckCircle size={20} color="#22c55e" />
              {t.completedIssues}
            </h3>
            {completedIssues.map(issue => (
              <div key={issue.issue_id} style={styles.completedIssueCard}>
                <div>{issue.issue_description}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Fixed: {new Date(issue.reported_date).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div style={styles.bottomActions}>
        <button
          onClick={handleSaveAsDraft}
          disabled={saving}
          style={{ ...styles.button, ...styles.secondaryButton }}
        >
          {saving ? t.saving : t.saveAsDraft}
        </button>
        <button
          onClick={handleCompleteInspection}
          disabled={saving}
          style={{ ...styles.button, ...styles.primaryButton }}
        >
          <Save size={20} />
          {saving ? t.saving : t.completeInspection}
        </button>
      </div>

      {/* Issue Creation Modal */}
      {showIssueModal && currentIssueItem && (
        <IssueCreationModal
          itemName={currentIssueItem.item_name}
          onSubmit={handleCreateIssue}
          onClose={() => {
            setShowIssueModal(false);
            setCurrentIssueItem(null);
          }}
          language={language}
        />
      )}

      {/* Fix Issue Modal */}
      {showFixIssueModal && currentFixingIssue && (
        <FixIssueModal
          issue={currentFixingIssue}
          onSubmit={handleFixIssue}
          onClose={() => {
            setShowFixIssueModal(false);
            setCurrentFixingIssue(null);
          }}
          language={language}
        />
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div style={styles.successOverlay}>
          <div style={styles.successCard}>
            <CheckSquare size={64} style={{ color: '#22c55e', margin: '0 auto 16px auto' }} />
            <h2 style={{ margin: '0 0 8px 0', color: '#22c55e' }}>{t.success}</h2>
            <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>
              {t.successMessage}
            </p>
            <button onClick={handleBackToHub} style={{ ...styles.button, ...styles.primaryButton }}>
              {t.backToHub}
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* Camera Component (Android only) */}
      {showCamera && (
        <CameraComponent
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
        />
      )}
    </div>
  );
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

// Service Due Indicator Component
interface ServiceDueIndicatorProps {
  item: TemplateItem;
  currentHours: number;
  language: 'en' | 'es';
}

const ServiceDueIndicator: React.FC<ServiceDueIndicatorProps> = ({ item, currentHours, language }) => {
  const t = translations[language];

  const lastServiceHours = item.last_service_hours || 0;
  const interval = item.service_interval_hours || 0;
  const hoursSinceService = currentHours - lastServiceHours;
  const hoursUntilDue = interval - hoursSinceService;

  let status: 'due_now' | 'due_soon' | 'ok';
  let backgroundColor: string;
  let textColor: string;
  let label: string;

  // For items never serviced
  if (!item.last_service_hours && item.last_service_hours !== 0) {
    status = 'due_now';
    backgroundColor = '#fef2f2';
    textColor = '#dc2626';
    label = `${t.serviceDueNow} (${t.neverServiced})`;
  } else if (hoursUntilDue <= 0) {
    // Overdue or due now
    status = 'due_now';
    backgroundColor = '#fef2f2';
    textColor = '#dc2626';
    label = `${t.serviceDueNow} (${Math.abs(hoursUntilDue)} ${t.hrsOverdue})`;
  } else if (hoursUntilDue <= 50) {
    // Due soon (within 50 hours)
    status = 'due_soon';
    backgroundColor = '#fefce8';
    textColor = '#ca8a04';
    label = `${t.serviceDueSoon} (${hoursUntilDue} ${t.hrs})`;
  } else {
    // Not due yet
    status = 'ok';
    backgroundColor = '#f0fdf4';
    textColor = '#16a34a';
    label = `${t.serviceOk} (${t.dueIn} ${hoursUntilDue} ${t.hrs})`;
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 10px',
      backgroundColor,
      borderRadius: '6px',
      marginBottom: '8px',
      fontSize: '13px',
      fontWeight: '600'
    }}>
      {status === 'due_now' && <AlertTriangle size={14} color={textColor} />}
      {status === 'due_soon' && <Clock size={14} color={textColor} />}
      {status === 'ok' && <CheckCircle size={14} color={textColor} />}
      <span style={{ color: textColor }}>{label}</span>
    </div>
  );
};

// Inspection Item Component
interface InspectionItemProps {
  item: TemplateItem;
  itemData: InspectionItemData;
  currentHours: number | null;
  onStatusChange: (status: ItemStatus) => void;
  onNotesChange: (notes: string) => void;
  onTakePhoto: () => void;
  onRemovePhoto: (index: number) => void;
  language: 'en' | 'es';
}

const InspectionItem: React.FC<InspectionItemProps> = ({
  item,
  itemData,
  currentHours,
  onStatusChange,
  onNotesChange,
  onTakePhoto,
  onRemovePhoto,
  language
}) => {
  const t = translations[language];
  const isServiceItem = item.item_type === 'service';

  const getStatusButtonStyle = (status: ItemStatus): React.CSSProperties => {
    const isActive = itemData.item_status === status;

    const baseStyle: React.CSSProperties = {
      flex: 1,
      padding: '8px 12px',
      border: '2px solid',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      transition: 'all 0.2s'
    };

    const statusStyles: Record<ItemStatus, React.CSSProperties> = {
      completed: {
        borderColor: isActive ? '#22c55e' : '#d1d5db',
        backgroundColor: isActive ? '#22c55e' : '#f9fafb',
        color: isActive ? 'white' : '#64748b'
      },
      cleaned: {
        borderColor: isActive ? '#3b82f6' : '#d1d5db',
        backgroundColor: isActive ? '#3b82f6' : '#f9fafb',
        color: isActive ? 'white' : '#64748b'
      },
      not_needed: {
        borderColor: isActive ? '#f59e0b' : '#d1d5db',
        backgroundColor: isActive ? '#f59e0b' : '#f9fafb',
        color: isActive ? 'white' : '#64748b'
      },
      issue_found: {
        borderColor: isActive ? '#ef4444' : '#d1d5db',
        backgroundColor: isActive ? '#ef4444' : '#f9fafb',
        color: isActive ? 'white' : '#64748b'
      },
      pending: {
        borderColor: '#d1d5db',
        backgroundColor: '#f9fafb',
        color: '#64748b'
      }
    };

    return { ...baseStyle, ...statusStyles[status] };
  };

  return (
    <div style={styles.checklistItem}>
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
          {item.item_name}
        </div>

        {/* Service Due Indicator - only for service items with intervals */}
        {isServiceItem && item.service_interval_hours && currentHours !== null && currentHours > 0 && (
          <ServiceDueIndicator
            item={item}
            currentHours={currentHours}
            language={language}
          />
        )}

        {/* Status Buttons - different based on item_type */}
        {isServiceItem ? (
          // SERVICE ITEMS: Changed / Not Changed / Issue Found
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <button
              onClick={() => onStatusChange('completed')}
              style={getStatusButtonStyle('completed')}
            >
              <CheckCircle size={16} />
              {t.serviceItemChanged}
            </button>
            {item.allow_clean && (
              <button
                onClick={() => onStatusChange('cleaned')}
                style={getStatusButtonStyle('cleaned')}
              >
                <Wind size={16} />
                {t.serviceItemCleaned}
              </button>
            )}
            <button
              onClick={() => onStatusChange('not_needed')}
              style={getStatusButtonStyle('not_needed')}
            >
              <Minus size={16} />
              {t.serviceItemNotChanged}
            </button>
            <button
              onClick={() => onStatusChange('issue_found')}
              style={getStatusButtonStyle('issue_found')}
            >
              <AlertTriangle size={16} />
              {t.issueFound}
            </button>
          </div>
        ) : (
          // CHECK ITEMS: Pass / Fail
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <button
              onClick={() => onStatusChange('completed')}
              style={getStatusButtonStyle('completed')}
            >
              <CheckCircle size={16} />
              {t.checkItemPass}
            </button>
            <button
              onClick={() => onStatusChange('issue_found')}
              style={getStatusButtonStyle('issue_found')}
            >
              <XCircle size={16} />
              {t.checkItemFail}
            </button>
          </div>
        )}

        {/* Notes */}
        {(item.requires_notes || itemData.item_notes) && (
          <textarea
            value={itemData.item_notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder={t.notesPlaceholder}
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '14px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              minHeight: '60px',
              fontFamily: 'inherit',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
        )}

        {/* Photos */}
        {item.requires_photo && (
          <div style={{ marginTop: '8px' }}>
            <button
              onClick={onTakePhoto}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Camera size={16} />
              {t.takePhoto}
            </button>

            {/* Photos already on the server */}
            {itemData.uploaded_count > 0 && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: '8px',
                padding: '4px 10px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#16a34a'
              }}>
                <CheckCircle size={14} />
                {itemData.uploaded_count} {t.uploadedBadge}
              </div>
            )}

            {/* Pending photo previews (not yet uploaded) */}
            {itemData.photos.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                {itemData.photos.map((photo, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <img
                      src={URL.createObjectURL(photo)}
                      alt={`Photo ${index + 1}`}
                      style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px' }}
                    />
                    <button
                      onClick={() => onRemovePhoto(index)}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Issue Card Component
interface IssueCardProps {
  issue: EquipmentIssue;
  onFix: () => void;
  language: 'en' | 'es';
}

const IssueCard: React.FC<IssueCardProps> = ({ issue, onFix, language }) => {
  const t = translations[language];

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
      case 'critical':
        return '#ef4444';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#22c55e';
      default:
        return '#64748b';
    }
  };

  return (
    <div style={styles.issueCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
            {issue.issue_description}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            {t.priority}: <span style={{ color: getPriorityColor(issue.priority), fontWeight: '600' }}>
              {issue.priority}
            </span>
          </div>
          {issue.parts_needed && (
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
              {t.partsNeeded}: {issue.parts_needed}
            </div>
          )}
          {issue.parts_status && issue.parts_status !== 'none' && (
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
              {t.partsStatus}: {issue.parts_status}
            </div>
          )}
        </div>
      </div>
      <button
        onClick={onFix}
        style={{
          padding: '6px 12px',
          backgroundColor: '#22c55e',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: '600',
          cursor: 'pointer',
          marginTop: '8px'
        }}
      >
        {t.markFixed}
      </button>
    </div>
  );
};

// Issue Creation Modal Component
interface IssueCreationModalProps {
  itemName: string;
  onSubmit: (data: any) => void;
  onClose: () => void;
  language: 'en' | 'es';
}

const IssueCreationModal: React.FC<IssueCreationModalProps> = ({ itemName, onSubmit, onClose, language }) => {
  const t = translations[language];
  const [description, setDescription] = useState(`Issue found: ${itemName}`);
  const [severity, setSeverity] = useState('medium');
  const [priority, setPriority] = useState('medium');
  const [partsNeeded, setPartsNeeded] = useState('');
  const [partsStatus, setPartsStatus] = useState('none');

  const handleSubmit = () => {
    onSubmit({
      description,
      severity,
      priority,
      parts_needed: partsNeeded,
      parts_status: partsStatus
    });
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={{ ...styles.modalContent, maxWidth: '500px' }}>
        <h2 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>{t.createIssue}</h2>

        <div style={{ marginBottom: '12px' }}>
          <label style={styles.label}>{t.issueDescription}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ ...styles.textarea, minHeight: '80px' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label style={styles.label}>{t.severity}</label>
            <CustomSelect
              value={severity}
              onChange={(value) => setSeverity(value)}
              placeholder={t.severity}
              options={[
                { value: 'low', label: t.low },
                { value: 'medium', label: t.medium },
                { value: 'high', label: t.high }
              ]}
            />
          </div>

          <div>
            <label style={styles.label}>{t.priority}</label>
            <CustomSelect
              value={priority}
              onChange={(value) => setPriority(value)}
              placeholder={t.priority}
              options={[
                { value: 'low', label: t.low },
                { value: 'medium', label: t.medium },
                { value: 'high', label: t.high }
              ]}
            />
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={styles.label}>{t.partsNeeded}</label>
          <input
            type="text"
            value={partsNeeded}
            onChange={(e) => setPartsNeeded(e.target.value)}
            style={styles.input}
            placeholder="e.g., New lights"
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={styles.label}>{t.partsStatus}</label>
          <CustomSelect
            value={partsStatus}
            onChange={(value) => setPartsStatus(value)}
            placeholder={t.partsStatus}
            options={[
              { value: 'none', label: 'None' },
              { value: 'need_parts', label: 'Need Parts' },
              { value: 'parts_ordered', label: 'Parts Ordered' },
              { value: 'waiting_on_parts', label: 'Waiting on Parts' },
              { value: 'parts_received', label: 'Parts Received' }
            ]}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{ ...styles.button, ...styles.secondaryButton, flex: 1 }}>
            {t.cancel}
          </button>
          <button onClick={handleSubmit} style={{ ...styles.button, ...styles.primaryButton, flex: 1 }}>
            {t.createIssue}
          </button>
        </div>
      </div>
    </div>
  );
};

// Fix Issue Modal Component
interface FixIssueModalProps {
  issue: EquipmentIssue;
  onSubmit: (fixDescription: string) => void;
  onClose: () => void;
  language: 'en' | 'es';
}

const FixIssueModal: React.FC<FixIssueModalProps> = ({ issue, onSubmit, onClose, language }) => {
  const t = translations[language];
  const [fixDescription, setFixDescription] = useState('');

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent}>
        <h2 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>{t.markFixed}</h2>
        <p style={{ margin: '0 0 12px 0', color: '#64748b' }}>{issue.issue_description}</p>

        <div style={{ marginBottom: '24px' }}>
          <label style={styles.label}>{t.fixDescription}</label>
          <textarea
            value={fixDescription}
            onChange={(e) => setFixDescription(e.target.value)}
            style={{ ...styles.textarea, minHeight: '80px' }}
            placeholder="Describe what was done to fix the issue..."
          />
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{ ...styles.button, ...styles.secondaryButton, flex: 1 }}>
            {t.cancel}
          </button>
          <button onClick={() => onSubmit(fixDescription)} style={{ ...styles.button, ...styles.primaryButton, flex: 1 }}>
            {t.markFixed}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '20px',
    paddingBottom: '400px',
    maxWidth: '800px',
    margin: '0 auto',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    backgroundColor: '#ea580c',
    color: 'white',
    padding: '16px',
    borderRadius: '12px 12px 0 0',
    marginBottom: '2px'
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px'
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: 'white',
    padding: '8px',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px'
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '600',
    flex: 1
  },
  equipmentInfo: {
    fontSize: '14px',
    opacity: 0.9
  },
  content: {
    backgroundColor: 'white',
    borderRadius: '0 0 12px 12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
  },
  formSection: {
    padding: '20px',
    borderBottom: '2px solid #f1f5f9'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '6px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    minHeight: '80px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  issuesSection: {
    padding: '20px',
    borderBottom: '2px solid #f1f5f9',
    backgroundColor: '#fef3c7'
  },
  issuesTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#92400e',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  issueCard: {
    backgroundColor: 'white',
    border: '2px solid #fbbf24',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '10px'
  },
  categorySection: {
    padding: '16px 20px',
    borderBottom: '2px solid #f1f5f9'
  },
  categoryTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '12px'
  },
  checklistItem: {
    backgroundColor: '#f8fafc',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '10px'
  },
  completedIssuesSection: {
    padding: '20px',
    borderTop: '2px solid #f1f5f9'
  },
  completedIssuesTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#22c55e',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  completedIssueCard: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #86efac',
    borderRadius: '6px',
    padding: '8px 12px',
    marginBottom: '8px',
    fontSize: '14px'
  },
  bottomActions: {
    backgroundColor: 'white',
    borderTop: '2px solid #e2e8f0',
    padding: '16px',
    display: 'flex',
    gap: '12px',
    maxWidth: '800px',
    margin: '20px auto 100px auto', // Add bottom margin for footer clearance
    position: 'relative'
  },
  button: {
    padding: '14px 20px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flex: 1
  },
  primaryButton: {
    backgroundColor: '#ea580c',
    color: 'white'
  },
  secondaryButton: {
    backgroundColor: '#64748b',
    color: 'white'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
    paddingTop: '10vh',
    paddingBottom: '40vh',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch'
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '400px',
    width: '100%',
    flexShrink: 0
  },
  successOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
    paddingTop: '10vh',
    paddingBottom: '40vh',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch'
  },
  successCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '32px',
    textAlign: 'center',
    maxWidth: '400px',
    width: '100%',
    flexShrink: 0
  },
  errorMessage: {
    position: 'fixed',
    bottom: '90px',
    left: '20px',
    right: '20px',
    backgroundColor: '#fef2f2',
    border: '2px solid #fca5a5',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    zIndex: 1000,
    maxWidth: '760px',
    margin: '0 auto'
  }
};
